import { Clock, Moon, Pause, Play, RefreshCw, Sparkles, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createFocusSession, dreamingRecall, updateFocusSession } from "../lib/api";

// RM-B "Mơ / Dreaming": a global focus timer + on-return recall card.
// Flow: start a focus session → at time-up an on-screen notice shows (does NOT auto-stop) →
// user stops manually → break → on "Quay lại" a passive recall card is built from the session's
// notes (or due-for-review notes after a long gap) via Workers AI.

type DreamState = "idle" | "running" | "break" | "loading" | "recall";
type DreamCard = { recall: string; reflect: string; encourage: string; noteCount: number; mode: string; source?: "workers-ai" | "fallback"; error?: string };

const DEFAULT_WORK_MINUTES = 25;

const LS = {
  state: "dream.state",
  start: "dream.start",
  stop: "dream.stop",
  work: "dream.work",
  context: "dream.context",
  sessionId: "dream.sessionId"
};

function read(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function write(key: string, value: string) {
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage failures
  }
}

function formatClock(totalSeconds: number) {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

type FocusDreamingProps = {
  onSessionsChanged?: () => void;
};

export function FocusDreaming({ onSessionsChanged }: FocusDreamingProps) {
  const [state, setState] = useState<DreamState>(() => {
    const persisted = read(LS.state);
    return persisted === "running" || persisted === "break" ? (persisted as DreamState) : "idle";
  });
  const [workMinutes, setWorkMinutes] = useState<number>(() => {
    const value = Number(read(LS.work));
    return Number.isFinite(value) && value > 0 ? value : DEFAULT_WORK_MINUTES;
  });
  const [startedAt, setStartedAt] = useState<string>(() => read(LS.start));
  const [stoppedAt, setStoppedAt] = useState<string>(() => read(LS.stop));
  const [context, setContext] = useState<string>(() => read(LS.context));
  const [card, setCard] = useState<DreamCard | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => read(LS.sessionId));
  const [error, setError] = useState("");
  const [, setTick] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const intervalRef = useRef<number | null>(null);

  // Tick once per second while a session is running so the countdown re-renders.
  useEffect(() => {
    if (state !== "running") {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state]);

  const startSession = () => {
    const now = new Date().toISOString();
    setStartedAt(now);
    setStoppedAt("");
    setCard(null);
    setSessionId("");
    setContext("");
    setError("");
    setState("running");
    write(LS.start, now);
    write(LS.stop, "");
    write(LS.sessionId, "");
    write(LS.context, "");
    write(LS.state, "running");
    write(LS.work, String(workMinutes));
  };

  const stopSession = async () => {
    const now = new Date().toISOString();
    setStoppedAt(now);
    setState("loading");
    setCard(null);
    setError("");
    write(LS.stop, now);
    write(LS.state, "loading");

    // Persist the session on stop; the Mơ card is generated immediately below.
    const elapsedSeconds = startedAt ? Math.max(0, Math.floor((Date.parse(now) - Date.parse(startedAt)) / 1000)) : 0;
    let savedSessionId = "";
    try {
      const result = await createFocusSession({
        startedAt: startedAt || now,
        stoppedAt: now,
        workMinutes,
        elapsedSeconds
      });
      const newId = result.data?.id ?? "";
      savedSessionId = newId;
      setSessionId(newId);
      write(LS.sessionId, newId);
      onSessionsChanged?.();
    } catch {
      // Persisting history is best-effort; never block the Mơ card.
    }

    try {
      const result = await dreamingRecall({
        mode: "session",
        since: startedAt || now,
        until: now,
        context: context || undefined
      });
      if (result.data) {
        setCard(result.data);
        setState("recall");
        write(LS.state, "recall");
        if (savedSessionId) {
          try {
            await updateFocusSession(savedSessionId, {
              context: context || undefined,
              mode: "session",
              noteCount: result.data.noteCount,
              recall: result.data.recall,
              reflect: result.data.reflect,
              encourage: result.data.encourage
            });
            onSessionsChanged?.();
          } catch {
            // Best-effort enrichment; the timing row is already saved.
          }
        }
      } else {
        setError("KhÃ´ng táº¡o Ä‘Æ°á»£c pháº§n MÆ¡. Thá»­ láº¡i.");
        setState("break");
        write(LS.state, "break");
      }
    } catch {
      setError("KhÃ´ng táº¡o Ä‘Æ°á»£c pháº§n MÆ¡. Thá»­ láº¡i.");
      setState("break");
      write(LS.state, "break");
    }
  };

  const comeBack = async () => {
    setState("loading");
    setError("");
    const mode: "session" | "due" = "session";
    const until = stoppedAt || new Date().toISOString();
    try {
      const result = await dreamingRecall({ mode, since: startedAt || undefined, until, context: context || undefined });
      if (result.data) {
        setCard(result.data);
        setState("recall");
        // Patch the saved session row with the Mơ card so it shows in history.
        if (sessionId) {
          try {
            await updateFocusSession(sessionId, {
              context: context || undefined,
              mode,
              noteCount: result.data.noteCount,
              recall: result.data.recall,
              reflect: result.data.reflect,
              encourage: result.data.encourage
            });
            onSessionsChanged?.();
          } catch {
            // Best-effort enrichment; the timing row is already saved.
          }
        }
      } else {
        setError("Không tạo được phần Mơ. Thử lại.");
        setState("break");
      }
    } catch {
      setError("Không tạo được phần Mơ. Thử lại.");
      setState("break");
    }
  };

  const dismiss = () => {
    setState("idle");
    setCard(null);
    setStartedAt("");
    setStoppedAt("");
    setContext("");
    setSessionId("");
    write(LS.state, "");
    write(LS.start, "");
    write(LS.stop, "");
    write(LS.context, "");
    write(LS.sessionId, "");
  };

  // ----- derived -----
  const elapsedSeconds = startedAt ? Math.floor((Date.now() - Date.parse(startedAt)) / 1000) : 0;
  const remaining = workMinutes * 60 - elapsedSeconds;
  const overtime = remaining <= 0;

  // ----- render -----
  if (state === "idle") {
    return (
      <div className="mb-3">
        {collapsed ? (
          <button
            className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-white text-xs font-bold text-slate-700 hover:bg-slate-50"
            type="button"
            aria-label="Mở focus timer"
            onClick={() => setCollapsed(false)}
          >
            <Clock size={14} aria-hidden="true" /> Phiên tập trung
          </button>
        ) : (
          <div className="w-full rounded-lg border border-line bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700">
                <Clock size={14} aria-hidden="true" /> Phiên tập trung
              </span>
              <button className="text-muted hover:text-slate-700" type="button" aria-label="Thu gọn" onClick={() => setCollapsed(true)}>
                <X size={14} aria-hidden="true" />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input
                className="h-8 w-16 rounded-lg border border-line bg-white px-2 text-sm text-ink"
                type="number"
                min={1}
                max={180}
                value={workMinutes}
                onChange={(event) => setWorkMinutes(Math.max(1, Math.min(180, Number(event.target.value) || DEFAULT_WORK_MINUTES)))}
                aria-label="Số phút làm việc"
              />
              <span className="text-xs text-muted">phút</span>
              <button
                className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700"
                type="button"
                onClick={startSession}
              >
                <Play size={14} aria-hidden="true" /> Bắt đầu
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (state === "running") {
    return (
      <div className="mb-3 w-full rounded-lg border border-line bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700">
            <Clock size={14} aria-hidden="true" /> Đang tập trung
          </span>
          {overtime ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">đã hết phiên</span> : null}
        </div>
        <div className={`mt-1 text-3xl font-bold tabular-nums ${overtime ? "text-amber-600" : "text-ink"}`}>
          {overtime ? `+${formatClock(-remaining)}` : formatClock(remaining)}
        </div>
        {overtime ? (
          <p className="mt-1 text-[11px] font-medium text-amber-700">Hết giờ phiên — cứ học tiếp, bấm Dừng khi xong.</p>
        ) : (
          <p className="mt-1 text-[11px] text-muted">Bấm Dừng để hệ thống tổng hợp note trong phiên và tạo phần Mơ.</p>
        )}
        <input
          className="mt-2 h-8 w-full rounded-lg border border-line bg-white px-2 text-xs text-ink placeholder:text-slate-400"
          value={context}
          onChange={(event) => {
            setContext(event.target.value);
            write(LS.context, event.target.value);
          }}
          placeholder="(tùy chọn) trọng tâm phiên học..."
          aria-label="Trọng tâm phiên học"
        />
        <button
          className="mt-2 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 text-xs font-bold text-rose-700 hover:bg-rose-100"
          type="button"
          onClick={() => void stopSession()}
        >
          <Square size={13} aria-hidden="true" /> Dừng phiên → Mơ
        </button>
      </div>
    );
  }

  if (state === "break" || state === "loading") {
    return (
      <div className="mb-3 w-full rounded-lg border border-indigo-200 bg-indigo-50 p-3 shadow-sm">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700">
          <Pause size={14} aria-hidden="true" /> {state === "loading" ? "Đang mơ" : "Mơ chưa xong"}
        </span>
        <p className="mt-1 text-[11px] text-indigo-700/80">
          {state === "loading" ? "Đang rà lại note trong phiên và nhờ AI tổng hợp." : "Không tạo được Mơ cho phiên này. Bạn có thể thử lại."}
        </p>
        <div className="mt-2 flex gap-2">
          <button
            className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-xs font-bold text-white hover:bg-indigo-700 disabled:bg-slate-300"
            type="button"
            disabled={state === "loading"}
            onClick={() => void comeBack()}
          >
            {state === "loading" ? <RefreshCw size={13} className="animate-spin" aria-hidden="true" /> : <Moon size={14} aria-hidden="true" />}
            {state === "loading" ? "Đang mơ..." : "Thử tạo lại Mơ"}
          </button>
          <button className="inline-flex h-8 items-center justify-center rounded-lg border border-line bg-white px-2 text-xs font-bold text-slate-600 hover:bg-slate-50" type="button" onClick={dismiss}>
            Bỏ qua
          </button>
        </div>
        {error ? <p className="mt-2 text-[11px] font-medium text-rose-600">{error}</p> : null}
      </div>
    );
  }

  // state === "recall"
  return (
    <div className="mb-3 w-full rounded-lg border border-indigo-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-indigo-700">
          <Moon size={16} aria-hidden="true" /> Mơ {card?.mode === "due" ? "· ôn lại" : ""}
        </span>
        <div className="flex items-center gap-2">
          {typeof card?.noteCount === "number" ? (
            <span className="rounded bg-indigo-50 px-2 py-0.5 text-[11px] font-bold text-indigo-700">{card.noteCount} note</span>
          ) : null}
          <button className="text-muted hover:text-slate-700" type="button" aria-label="Đóng" onClick={dismiss}>
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-muted">Nhớ lại</p>
          <p className="mt-0.5 whitespace-pre-line text-sm leading-6 text-ink">{card?.recall}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-muted">Ngẫm</p>
          <p className="mt-0.5 text-sm leading-6 text-slate-700">{card?.reflect}</p>
        </div>
        <div className="rounded-lg bg-indigo-50 px-3 py-2">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700">
            <Sparkles size={14} aria-hidden="true" /> {card?.encourage}
          </p>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand px-3 text-sm font-bold text-white hover:bg-blue-700"
          type="button"
          onClick={startSession}
        >
          <Play size={15} aria-hidden="true" /> Phiên mới
        </button>
        <button className="inline-flex h-9 items-center justify-center rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-600 hover:bg-slate-50" type="button" onClick={dismiss}>
          Đóng
        </button>
      </div>
    </div>
  );
}
